<template>
  <div
    class="provider-card"
    :class="{ selected: props.selected, clickable: props.clickable, loading: !props.title }"
    @click="handleClick">
    <template v-if="props.title">
      <div class="title">{{ title }}</div>
      <div class="image-container">
        <img :src="imageSrc" alt="Logo" class="logo" />
      </div>
      <div v-if="statusColor !== 'sync'" class="status-container">
        <div class="outer-circle">
          <div class="inner-circle"></div>
        </div>
        <span class="status-text">{{ statusText }}</span>
      </div>
      <div v-else class="status-container">
        <IconLoader class="loader-icon" :style="{ color: currentStatusColor }" size="16" />
        <span class="status-text">{{ statusText }}</span>
      </div>
    </template>
    <template v-else>
      <div class="placeholder-glow">
        <div class="placeholder placeholder-xs col-9 mb-3"></div>
      </div>
      <div class="placeholder-glow">
        <div class="placeholder col-10"></div>
      </div>
      <div class="placeholder-glow">
        <div class="placeholder placeholder-xs col-4"></div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import translator from '@/locale/translator';
import type { CampaignProvider } from '@/types/types';
import { IconLoader } from '@tabler/icons-vue';
import { computed } from 'vue';

const props = defineProps<{
  selected?: boolean;
  title: string;
  imageSrc: string;
  provider?: any;
  clickable?: boolean;
}>();

const emit = defineEmits<{
  select: [title: string];
}>();

const handleClick = () => {
  if (props.clickable !== false) {
    emit('select', props.title);
  }
};

const statusConfig = {
  new: { text: translator.get('campaigns.status.new'), color: '#D9D9D9', showLoader: false },
  confirmed: { text: translator.get('campaigns.status.confirmed'), color: '#007AFF', showLoader: false },
  sync: { text: translator.get('campaigns.status.sync'), color: '#8E939D', showLoader: true },
  active: { text: translator.get('campaigns.status.active'), color: '#1BD264', showLoader: false },
  paused: { text: translator.get('campaigns.status.paused'), color: '#FFB800', showLoader: false },
  error: { text: translator.get('campaigns.status.error'), color: '#F25325', showLoader: false },
  finished: { text: translator.get('campaigns.status.finished'), color: '#8E939D', showLoader: false },
};

// Computed property that determines the complete status
/**
 * -> New = base status, nothing has happened to the campaign yet
 * -> Confirmed = The campaign has been confirmed by the user, but has not started yet.
 * -> Sync = the campaign is being synchronised (task status of "waiting" or "in_progress")
 * -> Active = campaign.active = 1 and campaign.push_status = 1
 * -> Paused = campaign.active = 0 and campaign.push_status = 1
 * -> Finished = finished = 1
 * -> Error = task.status = error or push_status = -1
 */
const providerStatus = computed(() => {
  if (!props.provider) {
    return statusConfig.new;
  }

  const provider = props.provider as CampaignProvider;
  const status = provider.provider_status;

  if (status === 'waiting' || status === 'in_progress') {
    return statusConfig.sync;
  }

  if (status === 'error') {
    return statusConfig.error;
  }

  if (provider.finished === 1) {
    return statusConfig.finished;
  }

  if ((provider.active === 1 && provider.push_status === 1) || status === 'success') {
    return statusConfig.active;
  }

  if (provider.active === 0 && provider.push_status === 1) {
    return statusConfig.paused;
  }

  if (provider.active === 0 && provider.confirmed === true) {
    return statusConfig.confirmed;
  }

  return statusConfig.new;
});

const title = computed(() => {
  switch (props.title.toLowerCase()) {
    case 'googleads_display_ad':
    case 'googleads_display_ad_demand_gen':
      return 'Google Display';
    case 'googleads_search_ad':
      return 'Google Search';
    case 'facebook':
      return 'Facebook & Insta';
    default:
      return props.title;
  }
});

const statusColor = computed(() => (providerStatus.value.showLoader ? 'sync' : 'normal'));
const currentStatusColor = computed(() => providerStatus.value.color);
const statusText = computed(() => providerStatus.value.text);

// Computed property for the outer circle color (semi-transparent version)
const outerCircleColor = computed(() => {
  const baseColor = currentStatusColor.value;
  // Convert hex to rgba with 0.25 opacity
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, 0.25)`;
});
</script>
<style scoped lang="scss">
.provider-card {
  padding: 16px;
  width: 200px;
  height: 100px;
  border: 2px solid #eaecf0;
  border-radius: 10px;
  cursor: default;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: grid;
  grid-template-rows: subgrid;
  grid-row: 1 / 4;
  row-gap: 0px;
  margin-bottom: 10px;

  &.clickable {
    cursor: pointer;
  }

  &:hover.clickable {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    background: radial-gradient(circle at top left, rgba(0, 122, 255, 0.1), transparent 40%);
    border-color: var(--tw-color-primary);
    box-shadow: 0px 2px 6px 0px #00000029;
  }

  &.loading {
    cursor: wait;
  }
}

.title {
  font-size: 12px;
  font-weight: 600;
  line-height: 13px;
  margin-bottom: 10px;
}

.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  margin-bottom: 10px;
}

.image-container img {
  max-width: 100%;
  max-height: 100%;
  margin-right: auto;
  object-fit: contain;
}

.inner-circle {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: v-bind(currentStatusColor);
  transition: background-color 0.3s ease;
}

.outer-circle {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: v-bind(outerCircleColor);
  margin-right: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  transition: background-color 0.3s ease;
}

.status-container {
  display: flex;
  font-size: 10px;
  height: 10px;
  font-weight: 400;

  & .status-text {
    color: #8e939d;
  }
}

.loader-icon {
  animation: spin 2s linear infinite;
  margin-right: 6px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
